// Unified batting-order engine — the SINGLE source of truth for every surface
// that orders a lineup (Plan Game's LineupTab and the live-game Lineup Builder).
//
// Before this module, those two screens each had their own order logic and they
// drifted: Plan Game had the smart stats-driven modes ('Get Baserunners',
// 'Wear Down Pitcher') via suggestBattingOrder, while the Lineup Builder only had
// attribute-based sortBattingOrder modes. This collapses both into one engine and
// one mode list so they can never diverge again.

import { sortBattingOrder } from './lineup-engine';
import { suggestBattingOrder, BatterSeasonSignal } from './insights/season-signals';

// ── Defense fill modes ──────────────────────────────────────────────────────
// Shared so the Game Planner (StepDefense, multi-inning) and the live Lineup
// Builder (single inning, pre-game) show the same names for the same behavior.
// Names/descriptions match the live-game Auto Lineup menu in LiveGameEngine.tsx
// (kept in sync by hand — see AUTO_LINEUP_MODES there) so a coach sees identical
// language whether they're planning ahead or fixing the defense mid-game.
//
// All three modes regenerate the whole defense from scratch and work
// identically regardless of how many innings are being planned, so this is a
// flat list with no single-inning/multi-inning split. A fourth mode, 'fill_rest'
// ("keep Inning 1, generate the rest"), existed only for the multi-inning Game
// Planner and was removed 2026-09-10 at Dan's request — before that, the
// single-inning Lineup Builder had exposed it unfiltered, where it silently
// no-op'd to the same result as this list's first mode (defenseSettingsFor had
// no case for it), a selectable option with no distinct effect.

export interface DefenseModeDef {
  key: 'smart' | 'competitive' | 'rotate_all';
  label: string;
  description: string;
}

export const DEFENSE_MODES: DefenseModeDef[] = [
  {
    key: 'smart',
    label: 'Equal Bench Time',
    description: 'Uses your Fairness settings',
  },
  {
    key: 'rotate_all',
    label: 'New Positions Every Inning',
    description: 'No one repeats a position from last inning',
  },
  {
    key: 'competitive',
    label: 'Best Defenders',
    description: 'Strongest fielders win, fairness rules ignored',
  },
];

/** Apply a defense mode's effect to team settings before generateOptimalLineup. */
export function defenseSettingsFor(mode: string, settings: any): any {
  if (mode === 'competitive') {
    return { ...settings, fieldTimeEqualizerEnabled: false };
  }
  if (mode === 'rotate_all') {
    return { ...settings, fieldTimeEqualizerEnabled: true, noConsecutivePosition: true };
  }
  return { ...settings };
}

export interface OrderModeDef {
  /** Internal key — passed to orderBattingLineup(). */
  key: string;
  /** Short chip label. */
  label: string;
  /** True if the mode needs imported stat data (batterSignals) to run. */
  data: boolean;
}

/** The full mode set, shown identically on every surface. */
export const ORDER_MODES: OrderModeDef[] = [
  { key: 'OPS First',                 label: 'OPS First',   data: false },
  { key: 'Speed Spread',              label: 'Speed Spread', data: false },
  { key: 'Get Baserunners',           label: 'Get On Base', data: true  },
  { key: 'Wear Down Pitcher',         label: 'Wear Down',   data: true  },
  { key: 'Minimize Strikeouts',       label: "Min. K's",    data: false },
  { key: 'None',                      label: 'xOVR',        data: false },
  { key: 'Optimized',                 label: 'Optimized',   data: false },
  { key: 'Fairness',                  label: 'Fairness',    data: false },
  { key: 'Distribute Weaker Hitters', label: 'Distribute',  data: false },
];

/** One-line description of what each algorithm optimizes for. */
export const ORDER_MODE_DESCRIPTIONS: Record<string, string> = {
  'OPS First':                 'Highest OPS leads off, the way MLB builds a lineup now. Your best bat gets the most trips to the plate.',
  'Speed Spread':              'Spreads your fastest players through the order so there is a baserunning threat in front of every section.',
  'Get Baserunners':           'Ranks by season OBP + pitch count drawn. Best on-base threat leads off.',
  'Wear Down Pitcher':         'Prioritizes high pitch-per-PA players early to tire the opposing pitcher.',
  'Minimize Strikeouts':       'Lowest strikeout rate first. Keeps the ball in play and pressure on defense.',
  'None':                      'Ordered by xOVR, the overall player rating combining attributes and stats.',
  'Optimized':                 'Balances OBP, power, and contact across the order for maximum run production.',
  'Fairness':                  'Distributes batting opportunities evenly based on season PA totals.',
  'Distribute Weaker Hitters': 'Spreads lower-OPS players through the order rather than stacking them at the bottom.',
};

export interface OrderedPlayer {
  player: any;
  /** Human-readable justification for this player's slot. */
  reason: string;
}

/** True if this mode requires imported stat signals to run. */
export function modeNeedsData(modeKey: string): boolean {
  return !!ORDER_MODES.find(m => m.key === modeKey)?.data;
}

/**
 * Order a set of active players by the chosen mode, returning each player with a
 * short reason string. This is the exact logic that previously lived inline in
 * PlanGameView's LineupTab — now shared.
 *
 * @param activePlayers players already filtered to present (non-absent) and
 *        enriched with season `stats` where available.
 * @param batterSignals aggregated GameChanger signals (empty Map if none imported).
 * @param modeKey one of ORDER_MODES[].key.
 */
export function orderBattingLineup(
  activePlayers: any[],
  batterSignals: Map<string, BatterSeasonSignal>,
  modeKey: string,
): OrderedPlayer[] {
  if (modeKey === 'Get Baserunners' || modeKey === 'Wear Down Pitcher') {
    const sugg = suggestBattingOrder(
      batterSignals,
      activePlayers.map(p => p.id),
      (id: string) => activePlayers.find(p => p.id === id)?.name || '',
      modeKey === 'Wear Down Pitcher' ? 'weardown' : 'onbase',
    );
    const ranked = new Map(sugg.slots.map((s, i) => [s.playerId, { idx: i, reason: s.reason }]));
    return [...activePlayers]
      .sort((a, b) => (ranked.get(a.id)?.idx ?? 999) - (ranked.get(b.id)?.idx ?? 999))
      .map(p => ({ player: p, reason: ranked.get(p.id)?.reason || 'Limited data' }));
  }

  const sorted = modeKey === 'None'
    ? [...activePlayers].sort((a, b) => (b.ovr || 0) - (a.ovr || 0))
    : sortBattingOrder([...activePlayers], modeKey);

  return sorted.map((p): OrderedPlayer => {
    const ovr = p.ovr ?? 0;
    const contact = p.attributes?.hitting?.contact ?? 0;
    const eye = p.attributes?.hitting?.eye ?? 0;
    const pa = p.stats?.pa ?? 0;
    const bench = p.seasonBenchInnings ?? 0;
    switch (modeKey) {
      case 'None': return { player: p, reason: `xOVR ${ovr}` };
      case 'OPS First': return { player: p, reason: pa >= 3
        ? `${(p.stats?.ops || 0).toFixed(3)} OPS in ${pa} PA`
        : `Contact ${contact}/5, Power ${p.attributes?.hitting?.power ?? 0}/5 (no stat data yet)` };
      case 'Speed Spread': {
        const gp = p.stats?.gp || 0;
        const sb = p.stats?.sb || 0;
        const speed = p.attributes?.intangibles?.speed ?? 0;
        if (gp >= 2 && sb > 0) return { player: p, reason: `${sb} stolen ${sb === 1 ? 'base' : 'bases'} in ${gp} games` };
        return { player: p, reason: `Speed ${speed}/5` };
      }
      case 'Minimize Strikeouts': {
        if (pa >= 3) {
          const kPct = Math.round(((p.stats?.so || 0) / pa) * 100);
          return { player: p, reason: `${kPct}% K rate (${p.stats?.so || 0} K / ${pa} PA)` };
        }
        return { player: p, reason: `Contact ${contact}/5 (no stat data yet)` };
      }
      case 'Optimized': return { player: p, reason: pa >= 3
        ? `OBP ${(p.stats?.obp || 0).toFixed(3)}, OPS ${(p.stats?.ops || 0).toFixed(3)}`
        : `Contact ${contact}/5, Eye ${eye}/5` };
      case 'Fairness': return { player: p, reason: pa ? `${pa} PA this season${bench ? `, ${bench} bench innings` : ''}` : 'No PA recorded yet' };
      case 'Distribute Weaker Hitters': return { player: p, reason: pa >= 3
        ? `OPS ${(p.stats?.ops || 0).toFixed(3)}`
        : `xOVR ${ovr}` };
      default: return { player: p, reason: '' };
    }
  });
}
